/**
 * @description Main App component with router for the Time Clock portal
 *
 * Key features:
 * - Extracts companyId from URL path (/:companyId/*)
 * - Wraps authenticated views in ClockProvider context
 * - Routes between employee select, PIN entry, and main portal
 *
 * @notes
 * - URL pattern: portal.lynxpay.us/{company-uuid}
 * - Invalid/missing company UUID shows error state
 */

import React from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { ClockProvider } from './contexts/ClockContext';
import ClockRouter from './components/ClockRouter';

// Detect /portal prefix for SO subdomain routing (Worker proxies /portal/* to this app)
const basename = window.location.pathname.startsWith('/portal') ? '/portal' : '';

function App() {
  return (
    <BrowserRouter basename={basename}>
      <Routes>
        <Route path="/:companyId/*" element={<CompanyWrapper />} />
        <Route path="*" element={<InvalidRoute />} />
      </Routes>
    </BrowserRouter>
  );
}

function CompanyWrapper() {
  // With basename set, pathname is already stripped of the /portal prefix
  const segments = window.location.pathname.replace(basename, '').split('/');
  const companyId = segments[1];

  // Basic UUID validation
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  if (!uuidRegex.test(companyId)) {
    return <InvalidRoute />;
  }

  return (
    <ClockProvider companyId={companyId}>
      <ClockRouter />
    </ClockProvider>
  );
}

function InvalidRoute() {
  return (
    <div className="h-screen flex items-center justify-center bg-slate-900">
      <div className="text-center">
        <h1 className="text-2xl font-bold text-white mb-2">Time Clock</h1>
        <p className="text-slate-400">Invalid or missing company link.</p>
        <p className="text-slate-500 text-sm mt-2">Please use the link provided by your employer.</p>
      </div>
    </div>
  );
}

export default App;
